import { Timeline } from "antd";
import moment from "moment";

export type TAuditTimelineChange = {
  field: string;
  fromValue?: string | null;
  toValue?: string | null;
};

export type TAuditTimelineItem = {
  id: string;
  timestamp: string | null;
  label: string;
  entityLabel?: string;
  title?: string;
  description?: string;
  changes?: TAuditTimelineChange[];
  /** Shown as its own line after description / changes (e.g. who recorded the event). */
  recordedByLine?: string;
};

type TAuditTimelineProps = {
  items: TAuditTimelineItem[];
  emptyText?: string;
  heading?: string;
};

function formatTimelineValue(value?: string | null): string {
  if (value === null || value === undefined || `${value}`.trim() === "") {
    return "-";
  }
  return `${value}`;
}

export default function AuditTimeline(props: TAuditTimelineProps) {
  const {
    items,
    emptyText = "No timeline entries available.",
    heading = "Audit Timeline"
  } = props;

  return (
    <div className="bg-white border border-gray-200 rounded-md p-4 my-2 mx-3">
      <div className="text-sm font-semibold mb-3">{heading}</div>
      {items.length === 0 ? (
        <div className="text-sm text-gray-500">{emptyText}</div>
      ) : null}
      <div className="max-h-[320px] overflow-y-auto p-2">
        <Timeline
          mode="left"
          className="w-[700px] max-w-full [&_.ant-timeline-item-label]:text-left [&_.ant-timeline-item-content]:text-left"
        >
          {items.map((item) => (
            <Timeline.Item
              key={item.id}
              label={
                item.timestamp
                  ? moment(item.timestamp).format("MM-DD-YYYY hh:mm A")
                  : "N/A"
              }
            >
              <div className="text-sm font-semibold">{item.label}</div>
              {item.entityLabel || item.title ? (
                <div className="text-sm text-gray-800">
                  {item.entityLabel ? (
                    <span className="font-semibold">{item.entityLabel}:</span>
                  ) : null}{" "}
                  {item.title ? <span>{item.title}</span> : null}
                </div>
              ) : null}
              {item.description ? (
                <div className="text-sm text-gray-600">{item.description}</div>
              ) : null}
              {item.changes?.length ? (
                <div className="mt-2 space-y-1">
                  {item.changes.map((changeItem, index) => (
                    <div
                      key={`${item.id}-${changeItem.field}-${index}`}
                      className="text-sm text-gray-600"
                    >
                      <span className="font-semibold">{changeItem.field}:</span>{" "}
                      {formatTimelineValue(changeItem.fromValue)} -&gt;{" "}
                      {formatTimelineValue(changeItem.toValue)}
                    </div>
                  ))}
                </div>
              ) : null}
              {item.recordedByLine ? (
                <div className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-100">
                  {item.recordedByLine}
                </div>
              ) : null}
            </Timeline.Item>
          ))}
        </Timeline>
      </div>
    </div>
  );
}
